import {Component, OnDestroy, OnInit} from '@angular/core';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {ImageTypeEnum} from '../../shared/graphql/globalTypes';
import {UntypedFormControl} from '@angular/forms';
import {EMPTY, merge, of, Subscription} from 'rxjs';
import {catchError, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {
  GetDashboardUserStats,
  GetDashboardUserStats_me,
  GetDashboardUserStatsVariables,
  GetDashboardUserStats_me_tenant,
} from './__generated__/GetDashboardUserStats';
import {
  GetDashboardTemplateStats,
  GetDashboardTemplateStats_templates_edges_node,
  GetDashboardTemplateStatsVariables,
} from './__generated__/GetDashboardTemplateStats';
import {
  getSpecificTimeSelection,
  mapMonthSelection,
  mapTimeSelection,
  SpecificTimeSelection,
  TimeSelectionOption,
} from '../../shared/utils/date-sort.utils';
import {Router} from '@angular/router';
import {ErrorService} from '../../shared/services/error.service';
import {GetAllowedUsage} from './__generated__/GetAllowedUsage';
import {
  GetDashboardUserWithCreditsStats,
  GetDashboardUserWithCreditsStats_me,
  GetDashboardUserWithCreditsStatsVariables,
  GetDashboardUserWithCreditsStats_me_tenant,
} from './__generated__/GetDashboardUserWithCreditsStats';
import moment from 'moment';

const dashboardGetTemplateQuery = gql`
  query GetDashboardTemplateStats ($createdGte: DateTime, $createdLt: DateTime, $first: Int) {
    templates(orderByViews: {createdGte: $createdGte, createdLt: $createdLt}, first: $first) {
      edges {
        node {
          id
          title
          thumbnail
          created
          items {
            id
            type
          }

          statistics(periodGte: $createdGte, periodLt: $createdLt) {
            newContentCount
            newViewsCount
          }
        }
      }
    }
  }
`;

const dashboardGetUserQuery = gql`
  query GetDashboardUserStats($createdGte: DateTime!, $createdLt: DateTime!) {
    me {
      id
      firstName
      tenant {
        id
        allowedUsage
        created
        aiCreditsRemaining

        videoStats: statistics(contentType: Video, periodGte: $createdGte, periodLt: $createdLt) {
          newBillableViewsCount
        }
        gifStats: statistics(contentType: GIF, periodGte: $createdGte, periodLt: $createdLt) {
          newBillableViewsCount
        }
        imageStats: statistics(contentType: Image, periodGte: $createdGte, periodLt: $createdLt) {
          newBillableViewsCount
        }
      }
    }
  }
`;

const dashboardGetUserWithCreditsQuery = gql`
  query GetDashboardUserWithCreditsStats($yearMonth: YearMonth!) {
    me {
      id
      firstName
      tenant {
        id
        allowedUsage
        created
        aiCreditsRemaining

        videoCredits: creditUsage(contentType: Video, yearMonth: $yearMonth) {
          usedViewCredits
          viewCount
          usedRenderCredits
          renderCount
        }

        gifCredits: creditUsage(contentType: GIF, yearMonth: $yearMonth) {
          usedViewCredits
          viewCount
          usedRenderCredits
          renderCount
        }

        imageCredits: creditUsage(contentType: Image, yearMonth: $yearMonth) {
          usedViewCredits
          viewCount
          usedRenderCredits
          renderCount
        }
      }
    }
  }
`;

export const allowedUsageQuery = gql`
  query GetAllowedUsage {
    allowedUsage {
      month
      totalViews
      billingType
    }
  }
`;

export interface TimeSelectionOptionWithAllowedUsage extends TimeSelectionOption {
  allowedUsage: number;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  chartValue = 0;
  totalGeneratedCount = 0;

  user: GetDashboardUserStats_me | GetDashboardUserWithCreditsStats_me | null = null;
  statistics: {
    original: GetDashboardUserStats_me_tenant | null;
    credits: GetDashboardUserWithCreditsStats_me_tenant | null;
  } | null = null;
  templates: GetDashboardTemplateStats_templates_edges_node[] = [];
  ImageTypeEnum = ImageTypeEnum;

  usageMonthSelection = new UntypedFormControl(null);
  templateMonthSelection = new UntypedFormControl(null);
  monthSelectionOptions: TimeSelectionOptionWithAllowedUsage[] = [];
  specificTimeSelections: SpecificTimeSelection[] = [];
  loading = true;
  remainingAiCredits: number | null = null;

  private subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private router: Router,
    private errService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.specificTimeSelections = getSpecificTimeSelection();
    this.templateMonthSelection.setValue(this.specificTimeSelections[0]);

    const currentTemplateMonthSelection$ = merge(
      this.templateMonthSelection.valueChanges,
      of(this.templateMonthSelection.value),
    );

    const templates$ = currentTemplateMonthSelection$.pipe(
      map(filter => {
        if (filter === null) {
          return null;
        }
        return mapTimeSelection(filter);
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(filter => {
        return this.apollo
          .watchQuery<GetDashboardTemplateStats, GetDashboardTemplateStatsVariables>({
            query: dashboardGetTemplateQuery,
            variables: {
              createdGte: filter ? filter.gte : null,
              createdLt: filter ? filter.lt : null,
              first: 5,
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      this.apollo
        .query<GetAllowedUsage>({
          query: allowedUsageQuery,
        })
        .pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(output => {
          this.monthSelectionOptions = mapMonthSelection(output.data?.allowedUsage ?? []);
          this.usageMonthSelection.setValue(this.monthSelectionOptions[0]);

          this.watchStatisticsForMonthSelection();
        }),

      templates$.subscribe(output => {
        const templates = output.data?.templates ?? null;
        if (templates !== null) {
          this.templates = templates.edges.map(e => e.node);
          this.loading = false;
        }
      }),
    );
  }

  watchStatisticsForMonthSelection() {
    const currentUsageMonthSelection$ = merge(
      this.usageMonthSelection.valueChanges,
      of(this.usageMonthSelection.value),
    );

    const statistics$ = currentUsageMonthSelection$.pipe(
      map(filter => {
        return {...mapTimeSelection(filter), billingType: filter ? filter.billingType : 'original'};
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(filter => {
        if (filter.billingType !== 'original') {
          return this.apollo
            .watchQuery<
              GetDashboardUserWithCreditsStats,
              GetDashboardUserWithCreditsStatsVariables
            >({
              query: dashboardGetUserWithCreditsQuery,
              variables: {
                yearMonth: moment(filter.gte).format('YYYY-MM'),
              },
            })
            .valueChanges.pipe(
              map(res => ({...res, billingType: filter.billingType})),
              catchError(error => {
                this.errService.handleDefault(error);
                return EMPTY;
              }),
            );
        }

        return this.apollo
          .watchQuery<GetDashboardUserStats, GetDashboardUserStatsVariables>({
            query: dashboardGetUserQuery,
            variables: {
              createdGte: filter.gte,
              createdLt: filter.lt,
            },
          })
          .valueChanges.pipe(
            map(res => ({...res, billingType: filter.billingType})),
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      statistics$.subscribe(output => {
        const me = output.data?.me;
        if (me && output.billingType === 'original') {
          this.user = me;
          const tenant = me.tenant as GetDashboardUserStats_me_tenant;
          this.statistics = {original: tenant, credits: null};

          this.totalGeneratedCount =
            tenant.videoStats.newBillableViewsCount +
            tenant.gifStats.newBillableViewsCount +
            tenant.imageStats.newBillableViewsCount;
          this.chartValue =
            (this.totalGeneratedCount / this.usageMonthSelection.value.allowedUsage) * 100;

          if (this.remainingAiCredits === null) {
            this.remainingAiCredits = me.tenant.aiCreditsRemaining;
          }
          return;
        }

        if (me && output.billingType === '2026-credits') {
          this.user = me;
          const tenant = me.tenant as GetDashboardUserWithCreditsStats_me_tenant;
          this.statistics = {credits: tenant, original: null};

          if (this.remainingAiCredits === null) {
            this.remainingAiCredits = me.tenant.aiCreditsRemaining;
          }
        }
      }),
    );
  }

  createUser(): void {
    this.router.navigate(['create-user']);
  }

  toTemplate(): void {
    this.router.navigate(['templates']);
  }

  toContentWithTemplateItemId(id: string): void {
    this.router.navigate(['/content'], {queryParams: {templateId: id}});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
